import { useId, useRef, useEffect, createRef } from "react";

import PropTypes from "prop-types";

import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation } from "swiper";

import { GTMPushEvent } from "helpers/google-tag-manager";
import { gtmLoadInterval } from "src/helpers/google-tag-manager/gtm-load-interval";
import convertToUrl from "helpers/convert-to-url";

import { slugify } from "src/utils/string";

import SwiperWrapper from "@/src/components/swiper-wrapper";
import VerticalCard from "@/src/components/cards/vertical-card";

import styles from "./styles.module.css";

const SuggestedListings = ({ data }) => {
  if (!data?.response?.length) return null;

  const listingData = data?.response;
  const keyId = useId();

  const seenElements = useRef([]);
  const elementsRef = useRef(listingData?.map(() => createRef()));

  const suggestedListingType = data?.relatedListingType === "RECOMMENDED";
  const listingTitle = suggestedListingType ? "Bu İlana Bakanlar Bunlara da Baktı" : "Benzer İlanlar";
  const itemListName = suggestedListingType ? "ilan-detay/bunlara-da-baktı" : "ilan-detay/benzer-ilanlar";

  const generateEcommerceData = (item, index, searchId) => ({
    search_id: searchId,
    item_price: item.price?.value ? `${Math.floor(item.price.value)}.00` : "0",
    item_id: `${item.id}`,
    item_name: convertToUrl(item.title),
    currency: item.price?.currency,
    discount: 0.0,
    index: Number(index) + 1,
    item_brand: "ilan",
    item_category: slugify(item.category),
    item_category2: slugify(item.estateType),
    item_category3: slugify(item.location?.cityName),
    item_category4: slugify(item.location?.districtName),
    item_category5: slugify(item.location?.townName),
    item_list_id: suggestedListingType ? "A7b9K1m4Qz8xL2wP0vN5rY6cJ3hD7tF1sV8u" : "bnfJdnKiz7YjoGxKtBCmV1tOeKuva3ke",
    item_list_name: itemListName,
    item_variant: slugify(item.tradeType),
    location_id: item.location?.cityId?.toString() ?? "",
    price: 0.0,
    quantity: 1,
  });

  const handleClick = async (item, index) => {
    const gtmIdWrapper = localStorage.getItem("EjGtmId");
    const parsed = JSON.parse(gtmIdWrapper);
    const searchId = parsed.listingdetail;

    gtmLoadInterval(() => {
      GTMPushEvent({ ecommerce: null });
      GTMPushEvent({
        event: "select_item",
        ecommerce: {
          items: [generateEcommerceData(item, index, searchId)],
        },
      });
    });
  };

  const handleIntersection = (entries) => {
    entries.forEach(async (entry) => {
      const { isIntersecting } = entry;
      if (!isIntersecting) return;

      const id = entry.target.getAttribute("data-listing-card-index");
      const isElementSeen = seenElements.current.includes(id);
      if (isElementSeen) return;

      seenElements.current.push(id);
      const item = listingData[id];

      const gtmIdWrapper = localStorage.getItem("EjGtmId");
      const parsed = JSON.parse(gtmIdWrapper);
      const searchId = parsed.listingdetail;

      gtmLoadInterval(() => {
        GTMPushEvent({ ecommerce: null });
        GTMPushEvent({
          event: "view_item_list",
          ecommerce: {
            items: [generateEcommerceData(item, id, searchId)],
          },
        });
      });
    });
  };

  useEffect(() => {
    const observer = new IntersectionObserver((entries) => handleIntersection(entries), {
      root: null,
      rootMargin: "0px",
      threshold: 0.5,
    });

    elementsRef.current.forEach((element) => {
      if (element.current) {
        observer.observe(element.current);
      }
    });

    return () => {
      elementsRef.current.forEach((element) => {
        if (element.current) {
          observer.unobserve(element.current);
        }
      });
    };
  }, [listingData]);

  return (
    <div id="onerilen-ilanlar" data-scroll-selector className={styles.wrapper}>
      <p className={styles.title}>{listingTitle}</p>
      <div className={styles.sliderWrap}>
        <SwiperWrapper>
          <Swiper navigation={true} loop={false} modules={[Navigation]} slidesPerView={4}>
            <ul>
              {listingData?.map((item, index) => (
                <SwiperSlide key={`${keyId}-${index}`}>
                  <li>
                    <div ref={elementsRef.current[index]} data-listing-card-index={index}>
                      <VerticalCard
                        media={item.coverPhoto}
                        title={item.title}
                        list={item.quickInfo}
                        locationSummary={item.locationSummary}
                        location={item.location}
                        price={item.price}
                        url={item.url}
                        id={item.id}
                        isSuggestedComponent={true}
                        phoneNumber={item.phoneNumber}
                        onCardClick={() => handleClick(item, index)}
                        componentFlag={suggestedListingType ? "suggestedListings" : "similarListings"}
                      />
                    </div>
                  </li>
                </SwiperSlide>
              ))}
            </ul>
          </Swiper>
        </SwiperWrapper>
      </div>
    </div>
  );
};

SuggestedListings.propTypes = {
  data: PropTypes.object,
};

export default SuggestedListings;
